/**
 * Default Keyboard Shortcuts Registry
 *
 * Defines all default keyboard shortcuts for Phrasewright with smart
 * cross-platform compatibility and comprehensive coverage of app functionality.
 */

import {
  type ShortcutAction,
  type KeyCombination,
  type ShortcutCategory,
  type ShortcutContext,
} from '@/core/types/keyboard';

// Helper to create cross-platform key combinations
function createKeys(
  key: string,
  modifiers: string[] = [],
  alternatives?: KeyCombination[]
): KeyCombination {
  return {
    key,
    modifiers: modifiers as any[],
    alternatives,
  };
}

// Helper to create Mac + Windows alternatives
function createCrossPlatformKeys(
  key: string,
  macModifiers: string[] = [],
  winModifiers: string[] = []
): KeyCombination {
  const macKeys = createKeys(key, macModifiers);
  const winKeys = createKeys(key, winModifiers);

  return {
    ...macKeys,
    alternatives: [winKeys],
  };
}

/**
 * Create default shortcut action
 */
function createAction(
  id: string,
  label: string,
  category: ShortcutCategory,
  context: ShortcutContext,
  _keys: KeyCombination,
  handler: () => void,
  options: {
    description?: string;
    icon?: string;
    priority?: 'low' | 'normal' | 'high' | 'system';
    customizable?: boolean;
  } = {}
): ShortcutAction {
  return {
    id,
    label,
    description: options.description,
    category,
    context,
    priority: options.priority || 'normal',
    handler,
    customizable: options.customizable !== false,
    enabled: true,
    icon: options.icon,
  };
}

/**
 * Get all default shortcuts with their handlers
 */
export function getDefaultShortcuts(handlers: {
  // File operations
  newDocument: () => void;
  newPhrase: () => void;
  openFile: () => void;
  save: () => void;
  saveAs: () => void;
  closeFile: () => void;

  // Edit operations
  undo: () => void;
  redo: () => void;
  cut: () => void;
  copy: () => void;
  paste: () => void;
  selectAll: () => void;
  find: () => void;
  replace: () => void;

  // View operations
  toggleSidebar: () => void;
  togglePreview: () => void;
  toggleMinimap: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  toggleFullscreen: () => void;

  // Navigation
  goToLine: () => void;
  nextTab: () => void;
  prevTab: () => void;
  closeTab: () => void;

  // Workflow
  commandPalette: () => void;
  quickSearch: () => void;
  globalSearch: () => void;

  // Reference system
  insertReference: () => void;
  quickReference: () => void;
  goToReference: () => void;

  // Format
  bold: () => void;
  italic: () => void;
  insertCode: () => void;
  insertCodeBlock: () => void;
  insertHeader: (level: number) => void;
  insertList: () => void;
  insertOrderedList: () => void;
  insertLink: () => void;

  // System
  settings: () => void;
  help: () => void;
  shortcuts: () => void;
  devTools: () => void;
}): ShortcutAction[] {
  return [
    // === FILE OPERATIONS ===
    createAction(
      'file.new.document',
      'New Document',
      'file',
      'global',
      createCrossPlatformKeys('n', ['meta'], ['ctrl']),
      handlers.newDocument,
      {
        description: 'Create a new document file',
        icon: 'FileText',
        priority: 'high',
      }
    ),

    createAction(
      'file.new.phrase',
      'New Phrase',
      'file',
      'global',
      createCrossPlatformKeys('n', ['meta', 'shift'], ['ctrl', 'shift']),
      handlers.newPhrase,
      {
        description: 'Create a new phrase file',
        icon: 'Plus',
        priority: 'high',
      }
    ),

    createAction(
      'file.open',
      'Open File',
      'file',
      'global',
      createCrossPlatformKeys('o', ['meta'], ['ctrl']),
      handlers.openFile,
      {
        description: 'Open an existing file',
        icon: 'FolderOpen',
      }
    ),

    createAction(
      'file.save',
      'Save',
      'file',
      'global',
      createCrossPlatformKeys('s', ['meta'], ['ctrl']),
      handlers.save,
      {
        description: 'Save current file',
        icon: 'Save',
        priority: 'high',
      }
    ),

    createAction(
      'file.save.as',
      'Save As',
      'file',
      'global',
      createCrossPlatformKeys('s', ['meta', 'shift'], ['ctrl', 'shift']),
      handlers.saveAs,
      {
        description: 'Save file with new name',
        icon: 'Save',
      }
    ),

    createAction(
      'file.close',
      'Close File',
      'file',
      'global',
      createCrossPlatformKeys('w', ['meta'], ['ctrl']),
      handlers.closeFile,
      {
        description: 'Close current file',
        icon: 'X',
      }
    ),

    // === EDIT OPERATIONS ===
    createAction(
      'edit.undo',
      'Undo',
      'edit',
      'editor',
      createCrossPlatformKeys('z', ['meta'], ['ctrl']),
      handlers.undo,
      {
        description: 'Undo last action',
        icon: 'Undo',
        priority: 'high',
      }
    ),

    createAction(
      'edit.redo',
      'Redo',
      'edit',
      'editor',
      createCrossPlatformKeys('z', ['meta', 'shift'], ['ctrl', 'shift']),
      handlers.redo,
      {
        description: 'Redo last undone action',
        icon: 'Redo',
        priority: 'high',
      }
    ),

    createAction(
      'edit.cut',
      'Cut',
      'edit',
      'editor',
      createCrossPlatformKeys('x', ['meta'], ['ctrl']),
      handlers.cut,
      {
        description: 'Cut selected text',
        icon: 'Scissors',
        priority: 'system',
        customizable: false,
      }
    ),

    createAction(
      'edit.copy',
      'Copy',
      'edit',
      'editor',
      createCrossPlatformKeys('c', ['meta'], ['ctrl']),
      handlers.copy,
      {
        description: 'Copy selected text',
        icon: 'Copy',
        priority: 'system',
        customizable: false,
      }
    ),

    createAction(
      'edit.paste',
      'Paste',
      'edit',
      'editor',
      createCrossPlatformKeys('v', ['meta'], ['ctrl']),
      handlers.paste,
      {
        description: 'Paste from clipboard',
        icon: 'Paste',
        priority: 'system',
        customizable: false,
      }
    ),

    createAction(
      'edit.select.all',
      'Select All',
      'edit',
      'editor',
      createCrossPlatformKeys('a', ['meta'], ['ctrl']),
      handlers.selectAll,
      {
        description: 'Select all text',
        icon: 'MousePointer',
        priority: 'system',
      }
    ),

    createAction(
      'edit.find',
      'Find',
      'edit',
      'editor',
      createCrossPlatformKeys('f', ['meta'], ['ctrl']),
      handlers.find,
      {
        description: 'Find text in current file',
        icon: 'Search',
        priority: 'high',
      }
    ),

    createAction(
      'edit.replace',
      'Replace',
      'edit',
      'editor',
      createCrossPlatformKeys('h', ['meta'], ['ctrl']),
      handlers.replace,
      {
        description: 'Find and replace text',
        icon: 'Replace',
      }
    ),

    // === VIEW OPERATIONS ===
    createAction(
      'view.sidebar.toggle',
      'Toggle Sidebar',
      'view',
      'global',
      createCrossPlatformKeys('b', ['meta'], ['ctrl']),
      handlers.toggleSidebar,
      {
        description: 'Show/hide file sidebar',
        icon: 'SidebarOpen',
      }
    ),

    createAction(
      'view.preview.toggle',
      'Toggle Preview',
      'view',
      'editor',
      createCrossPlatformKeys('p', ['meta', 'shift'], ['ctrl', 'shift']),
      handlers.togglePreview,
      {
        description: 'Show/hide markdown preview',
        icon: 'Eye',
      }
    ),

    createAction(
      'view.minimap.toggle',
      'Toggle Minimap',
      'view',
      'editor',
      createCrossPlatformKeys('m', ['meta'], ['ctrl']),
      handlers.toggleMinimap,
      {
        description: 'Show/hide editor minimap',
        icon: 'Map',
      }
    ),

    createAction(
      'view.zoom.in',
      'Zoom In',
      'view',
      'global',
      createCrossPlatformKeys('=', ['meta'], ['ctrl']),
      handlers.zoomIn,
      {
        description: 'Increase zoom level',
        icon: 'ZoomIn',
      }
    ),

    createAction(
      'view.zoom.out',
      'Zoom Out',
      'view',
      'global',
      createCrossPlatformKeys('-', ['meta'], ['ctrl']),
      handlers.zoomOut,
      {
        description: 'Decrease zoom level',
        icon: 'ZoomOut',
      }
    ),

    createAction(
      'view.zoom.reset',
      'Reset Zoom',
      'view',
      'global',
      createCrossPlatformKeys('0', ['meta'], ['ctrl']),
      handlers.resetZoom,
      {
        description: 'Reset zoom to default',
        icon: 'RotateCcw',
      }
    ),

    createAction(
      'view.fullscreen.toggle',
      'Toggle Fullscreen',
      'view',
      'global',
      createKeys('F11'),
      handlers.toggleFullscreen,
      {
        description: 'Enter/exit fullscreen mode',
        icon: 'Maximize',
      }
    ),

    // === NAVIGATION ===
    createAction(
      'nav.goto.line',
      'Go to Line',
      'navigation',
      'editor',
      createCrossPlatformKeys('g', ['meta'], ['ctrl']),
      handlers.goToLine,
      {
        description: 'Jump to specific line number',
        icon: 'CornerDownRight',
      }
    ),

    createAction(
      'nav.tab.next',
      'Next Tab',
      'navigation',
      'global',
      createCrossPlatformKeys('Tab', ['ctrl'], ['ctrl']),
      handlers.nextTab,
      {
        description: 'Switch to next open file',
        icon: 'ChevronRight',
      }
    ),

    createAction(
      'nav.tab.prev',
      'Previous Tab',
      'navigation',
      'global',
      createCrossPlatformKeys('Tab', ['ctrl', 'shift'], ['ctrl', 'shift']),
      handlers.prevTab,
      {
        description: 'Switch to previous open file',
        icon: 'ChevronLeft',
      }
    ),

    createAction(
      'nav.tab.close',
      'Close Tab',
      'navigation',
      'global',
      createCrossPlatformKeys('w', ['meta'], ['ctrl']),
      handlers.closeTab,
      {
        description: 'Close current tab',
        icon: 'X',
      }
    ),

    // === WORKFLOW ===
    createAction(
      'workflow.command.palette',
      'Command Palette',
      'workflow',
      'global',
      createCrossPlatformKeys('k', ['meta'], ['ctrl']),
      handlers.commandPalette,
      {
        description: 'Open command palette',
        icon: 'Command',
        priority: 'high',
      }
    ),

    createAction(
      'workflow.search.quick',
      'Quick Search',
      'workflow',
      'global',
      createCrossPlatformKeys('p', ['meta'], ['ctrl']),
      handlers.quickSearch,
      {
        description: 'Quick file search',
        icon: 'Search',
        priority: 'high',
      }
    ),

    createAction(
      'workflow.search.global',
      'Global Search',
      'workflow',
      'global',
      createCrossPlatformKeys('f', ['meta', 'shift'], ['ctrl', 'shift']),
      handlers.globalSearch,
      {
        description: 'Search across all files',
        icon: 'SearchCheck',
      }
    ),

    // === REFERENCE SYSTEM ===
    createAction(
      'reference.insert',
      'Insert Reference',
      'reference',
      'editor',
      createCrossPlatformKeys('@', []),
      handlers.insertReference,
      {
        description: 'Insert @ reference',
        icon: 'At',
        priority: 'high',
      }
    ),

    createAction(
      'reference.quick',
      'Quick Reference',
      'reference',
      'editor',
      createCrossPlatformKeys('r', ['meta', 'shift'], ['ctrl', 'shift']),
      handlers.quickReference,
      {
        description: 'Quick reference insertion',
        icon: 'Link',
      }
    ),

    createAction(
      'reference.goto',
      'Go to Reference',
      'reference',
      'editor',
      createKeys('F12'),
      handlers.goToReference,
      {
        description: 'Navigate to reference definition',
        icon: 'ExternalLink',
      }
    ),

    // === FORMATTING ===
    createAction(
      'format.bold',
      'Bold',
      'format',
      'editor',
      createCrossPlatformKeys('b', ['meta'], ['ctrl']),
      handlers.bold,
      {
        description: 'Toggle bold formatting',
        icon: 'Bold',
      }
    ),

    createAction(
      'format.italic',
      'Italic',
      'format',
      'editor',
      createCrossPlatformKeys('i', ['meta'], ['ctrl']),
      handlers.italic,
      {
        description: 'Toggle italic formatting',
        icon: 'Italic',
      }
    ),

    createAction(
      'format.code',
      'Inline Code',
      'format',
      'editor',
      createCrossPlatformKeys('`', ['meta'], ['ctrl']),
      handlers.insertCode,
      {
        description: 'Insert inline code',
        icon: 'Code',
      }
    ),

    createAction(
      'format.code.block',
      'Code Block',
      'format',
      'editor',
      createCrossPlatformKeys('c', ['meta', 'shift'], ['ctrl', 'shift']),
      handlers.insertCodeBlock,
      {
        description: 'Insert code block',
        icon: 'FileCode',
      }
    ),

    // Headers H1-H6
    ...Array.from({ length: 6 }, (_, i) => {
      const level = i + 1;
      return createAction(
        `format.header.${level}`,
        `Header ${level}`,
        'format',
        'editor',
        createCrossPlatformKeys(level.toString(), ['meta'], ['ctrl']),
        () => handlers.insertHeader(level),
        {
          description: `Insert H${level} header`,
          icon: `Heading${level}`,
        }
      );
    }),

    createAction(
      'format.list',
      'Bullet List',
      'format',
      'editor',
      createCrossPlatformKeys('l', ['meta', 'shift'], ['ctrl', 'shift']),
      handlers.insertList,
      {
        description: 'Insert bullet list',
        icon: 'List',
      }
    ),

    createAction(
      'format.list.ordered',
      'Numbered List',
      'format',
      'editor',
      createCrossPlatformKeys(
        'l',
        ['meta', 'shift', 'alt'],
        ['ctrl', 'shift', 'alt']
      ),
      handlers.insertOrderedList,
      {
        description: 'Insert numbered list',
        icon: 'ListOrdered',
      }
    ),

    createAction(
      'format.link',
      'Insert Link',
      'format',
      'editor',
      createCrossPlatformKeys('k', ['meta'], ['ctrl']),
      handlers.insertLink,
      {
        description: 'Insert markdown link',
        icon: 'Link',
      }
    ),

    // === SYSTEM ===
    createAction(
      'system.settings',
      'Settings',
      'system',
      'global',
      createCrossPlatformKeys(',', ['meta'], ['ctrl']),
      handlers.settings,
      {
        description: 'Open settings panel',
        icon: 'Settings',
      }
    ),

    createAction(
      'system.help',
      'Help',
      'system',
      'global',
      createKeys('?'),
      handlers.help,
      {
        description: 'Show help and shortcuts',
        icon: 'HelpCircle',
      }
    ),

    createAction(
      'system.shortcuts',
      'Keyboard Shortcuts',
      'system',
      'global',
      createCrossPlatformKeys('/', ['meta'], ['ctrl']),
      handlers.shortcuts,
      {
        description: 'Show keyboard shortcuts help',
        icon: 'Keyboard',
      }
    ),

    createAction(
      'system.dev.tools',
      'Developer Tools',
      'system',
      'global',
      createKeys('F12'),
      handlers.devTools,
      {
        description: 'Open browser developer tools',
        icon: 'Wrench',
        customizable: false,
      }
    ),
  ].flat(); // Flatten to handle the header array spread
}

/**
 * Get shortcuts grouped by category
 */
export function getShortcutsByCategory(
  shortcuts: ShortcutAction[]
): Record<string, ShortcutAction[]> {
  return shortcuts.reduce(
    (acc, shortcut) => {
      if (!acc[shortcut.category]) {
        acc[shortcut.category] = [];
      }
      acc[shortcut.category].push(shortcut);
      return acc;
    },
    {} as Record<string, ShortcutAction[]>
  );
}

/**
 * Get category display names
 */
export const CATEGORY_NAMES: Record<ShortcutCategory, string> = {
  file: 'File Operations',
  edit: 'Editing',
  view: 'View & Layout',
  navigation: 'Navigation',
  format: 'Text Formatting',
  reference: '@ Reference System',
  workflow: 'Workflow & Search',
  system: 'System & Help',
  custom: 'Custom Shortcuts',
};
